import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Grid,
  Card,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  Search,
  Build,
  Plumbing,
  ElectricalServices,
  Construction,
  FormatPaint,
  HomeRepairService,
  Yard,
  CleaningServices,
  DirectionsCar,
  Restaurant,
  Security,
  AcUnit,
  Computer,
  PhoneAndroid,
  ContentCut,
  BuildCircle,
  Handyman,
  Person,
  LocalShipping,
  Engineering,
  ChildCare,
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  Email,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import Api from '../api/userApi';
import { toast } from 'react-toastify';
import HomeHeroSearch from '../components/Home/HomeHeroSearch';
import useGeolocation from '../hooks/useGeolocation';
import LocationSelectDialog from '../components/Home/LocationSelectDialog';
import ServiceSearchDialog from '../components/Home/ServiceSearchDialog';
import ServicesSection from '../components/Home/ServicesSection';
import AvailableWorkersSection from '../components/Home/AvailableWorkersSection';
import FeaturedServicesSection from '../components/Home/FeaturedServicesSection';
import JobRequestsSection from '../components/Home/JobRequestsSection';
import SkillLabel from '../components/Common/SkillLabel';
import SEO from '../components/Common/SEO';
import TranslatedText from '../components/Common/TranslatedText';

// Import images (used by carousel only) — optimized WebP (~30KB each vs ~1.5MB PNG)
import painterImage from '../images/painter.webp';
import plumberImage from '../images/plumber.webp';
import electricianImage from '../images/electrician.webp';
import gardenerImage from '../images/gardener.webp';
import cookImage from '../images/Cook.webp';
import mechanicImage from '../images/Mechanicel.webp';
import welderImage from '../images/welder.webp';
import computerTechImage from '../images/ComputerTechnician.webp';
import farmerImage from '../images/FarmerAgriculturalWorker.webp';

// Below-the-fold components are code-split so they don't block initial render.
const TestimonialsSection = lazy(() => import('../components/Home/TestimonialsSection'));
const SupportChatbot = lazy(() => import('../components/Chatbot/SupportChatbot'));

const Home = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading, isAuthenticated, hasToken, logout, isBasicProfileComplete } = useAuth();

  // GPS is used ONLY for the explicit "Near Me" action (no map, free HTML5 API).
  const { requestLocation, requesting: nearMeRequesting } = useGeolocation();

  // Match Navbar's auth gating behavior to avoid brief "guest" flicker while token hydrates.
  const isLoggedIn = Boolean(user || (hasToken && !loading) || isAuthenticated);

  // Handle Google OAuth callback (redirect now goes to / instead of /register)
  useEffect(() => {
    const status = searchParams.get('google');
    const provider = 'google';

    if (!status) return;

    const handleOAuthCallback = async () => {
      if (status === 'success') {
        const tempToken = searchParams.get('token');
        const authData = searchParams.get('data');
        if (!tempToken || !authData) {
          toast.error(`Invalid ${provider} authentication response.`);
          setSearchParams({});
          return;
        }
        try {
          const exchangeUrl = Api.authGoogleExchange;
          const response = await axios.post(exchangeUrl, { tempToken, authData });
          const { token, accessToken, refreshToken, user: userData } = response.data;
          const finalAccessToken = accessToken || token;
          if (!finalAccessToken || !userData) {
            toast.error(`Failed to complete ${provider} login.`);
            setSearchParams({});
            return;
          }
          localStorage.setItem('token', finalAccessToken);
          if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${finalAccessToken}`;
          toast.success('Login successful!');
          setSearchParams({});
          // Redirect back to the page the user was on before Google OAuth (if any).
          // If there is no explicit action-based redirect, land on home.
          const loginRedirect = sessionStorage.getItem('loginRedirect');
          sessionStorage.removeItem('loginRedirect');
          const pendingBooking = localStorage.getItem('pendingBookingUrl');

          // Never redirect back to complete-profile or register after OAuth —
          // profile completion is only triggered by booking / become-worker actions.
          const safeRedirect = loginRedirect && !/\/(complete-profile|complete-worker-profile|register|login)(\?|$)/i.test(loginRedirect)
            ? loginRedirect
            : null;

          if (safeRedirect) {
            window.location.href = safeRedirect;
          } else if (pendingBooking) {
            // Google OAuth lost the sessionStorage chain — rebuild it from the stored booking URL
            if (!userData?.isBasicProfileComplete) {
              window.location.href = `/complete-profile?redirect=${encodeURIComponent(pendingBooking)}`;
            } else {
              localStorage.removeItem('pendingBookingUrl');
              window.location.href = pendingBooking;
            }
          } else {
            window.location.href = '/';
          }
        } catch (err) {
          console.error(`${provider} token exchange error:`, err);
          toast.error(`Failed to complete ${provider} login.`);
          setSearchParams({});
        }
      } else if (status === 'error') {
        const errorMessage = searchParams.get('message') || `${provider} authentication failed.`;
        toast.error(errorMessage);
        setSearchParams({});
      }
    };

    handleOAuthCallback();
  }, [searchParams, setSearchParams]);

  // Handle ATOZAS SSO callback result. The application JWT is restored by
  // AuthContext via /auth/atozas/me on load; here we only surface the outcome
  // to the user and strip the query param from the URL.
  useEffect(() => {
    const status = searchParams.get('atozas');
    if (!status) return;
    if (status === 'success') {
      toast.success('Login successful!');
    } else if (status === 'error') {
      toast.error(searchParams.get('message') || 'ATOZAS authentication failed.');
    }
    setSearchParams({});
  }, [searchParams, setSearchParams]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };
  const [location, setLocation] = useState('');
  const [service, setService] = useState(''); // For service/category dropdown
  const [skillInputText, setSkillInputText] = useState(''); // Tracks typed text in skill Autocomplete (may differ from service when user types without selecting)
  const [searchName, setSearchName] = useState(''); // For name search textbox
  const [minTeamSize, setMinTeamSize] = useState(1); // People needed (1 = normal worker)
  const [villages, setVillages] = useState([]);
  const [locationSkills, setLocationSkills] = useState([]); // skill names for selected location
  const [masterSkills, setMasterSkills] = useState([]); // master skill names from /api/skills (includes newly added)
  const [locationWorkerNames, setLocationWorkerNames] = useState([]); // worker fullNames for selected location
  const [testimonials, setTestimonials] = useState([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);
  const [jobRequests, setJobRequests] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  // Home no longer uses enquiry popups; enquiry happens on a dedicated screen.
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);

  // Memoize these so hooks depending on them don't re-run every render.
  const services = React.useMemo(() => ([
    { name: 'Plumber', icon: Plumbing },
    { name: 'Electrician', icon: ElectricalServices },
    { name: 'Carpenter', icon: Construction },
    { name: 'Painter', icon: FormatPaint },
    { name: 'Mason', icon: HomeRepairService },
    { name: 'Gardener', icon: Yard },
    { name: 'Cleaning', icon: CleaningServices },
    { name: 'Driver', icon: DirectionsCar },
    { name: 'Cook', icon: Restaurant },
    { name: 'Security Guard', icon: Security },
    { name: 'AC Repair', icon: AcUnit },
    { name: 'Computer Repair', icon: Computer },
    { name: 'Mobile Repair', icon: PhoneAndroid },
    { name: 'Tailor', icon: ContentCut },
    { name: 'Mechanic', icon: BuildCircle },
    { name: 'Welder', icon: Handyman },
    { name: 'Helper', icon: Person },
    { name: 'Delivery Person', icon: LocalShipping },
    { name: 'Daily Labour', icon: Engineering },
    { name: 'Babysitter', icon: ChildCare },
    { name: 'Baby Sitter', icon: ChildCare },
  ]), []);

  // Popular Services are now managed from Admin + served via API (no UI changes).
  const [featuredServices, setFeaturedServices] = useState([]);
  const [popularServices, setPopularServices] = useState([]);


  // Auto-scrolling images for carousel.
  // Memoized (image imports are stable) so the arrays aren't rebuilt on every
  // render — otherwise the carousel's list re-creates its keys each time Home
  // state (search inputs, dialogs) changes.
  const duplicatedImages = React.useMemo(() => {
    const carouselImages = [
      painterImage,
      plumberImage,
      electricianImage,
      gardenerImage,
      cookImage,
      mechanicImage,
      welderImage,
      computerTechImage,
      farmerImage,
      painterImage, // Add some duplicates for longer scroll
      plumberImage,
      electricianImage
    ];
    // Duplicate images for seamless infinite scroll
    return [...carouselImages, ...carouselImages];
  }, []);

  const scrollContainerRef = useRef(null);
  const [isScrolling, setIsScrolling] = useState(true);

  const fetchVillages = async () => {
    try {
      const response = await axios.get(Api.workerVillages);
      setVillages(response.data.villages || []);
    } catch (error) {
      console.error('Error fetching villages:', error);
      setVillages([]);
    }
  };

  const fetchFeaturedServices = async () => {
    try {
      const response = await axios.get(Api.featuredServices);
      const list = Array.isArray(response.data?.featuredServices)
        ? response.data.featuredServices
        : [];
      const base = Api.main;
      const baseUrl = (() => {
        try {
          return new URL(base);
        } catch {
          return null;
        }
      })();

      const normalized = list.map((item) => {
        const raw = item?.image;
        if (typeof raw !== 'string' || raw.length === 0) return item;

        // If backend returns a relative uploads path, always anchor it to Api.main.
        if (raw.startsWith('/uploads/') && baseUrl) {
          return { ...item, image: `${baseUrl.origin}${raw}` };
        }

        // If backend returns an absolute URL but protocol/host mismatch exists,
        // prefer the Api.main origin (prevents mixed-content + wrong-host issues).
        if (baseUrl) {
          try {
            const u = new URL(raw);
            if (u.host === baseUrl.host) {
              return { ...item, image: `${baseUrl.origin}${u.pathname}${u.search}` };
            }
          } catch {
            // ignore invalid URL
          }
        }

        return item;
      });

      setFeaturedServices(normalized);
    } catch (error) {
      console.error('Error fetching featured services:', error);
      setFeaturedServices([]);
    }
  };

  const fetchPopularServices = async () => {
    try {
      const response = await axios.get(Api.popularServices);
      const list = Array.isArray(response.data?.popularServices) ? response.data.popularServices : [];
      setPopularServices(list);
    } catch (error) {
      console.error('Error fetching popular services:', error);
    }
  };

  const fetchMasterSkills = async () => {
    try {
      const resp = await axios.get(Api.skills);
      const names = Array.isArray(resp.data?.skills) ? resp.data.skills : [];
      setMasterSkills(
        names
          .map((n) => (typeof n === 'string' ? n : n?.name || ''))
          .map((s) => String(s || '').trim())
          .filter(Boolean)
      );
    } catch (error) {
      console.error('Error fetching master skills:', error);
      setMasterSkills([]);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${Api.testimonials}?limit=8`);
      setTestimonials(response.data.testimonials || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      // Fallback to 8 testimonials so 4 pages × 2 per page = 4 dots
      setTestimonials([
        {
          name: 'Sumitra Reddy',
          title: 'Hiring Manager, Telangana',
          experience: "I've been searching for office staff to hire immediately and UlcaApp matched the right job profiles from my area. Got many suitable candidates to choose from. Best platform to hire!"
        },
        {
          name: 'Ravi Kumar',
          title: 'Floor Manager, Tamil Nadu',
          experience: 'Working as factory manager, I got staff within one week from my own district. UlcaApp is truly local and very reliable.'
        },
        {
          name: 'Anjali Desai',
          title: 'Hiring Manager, Telangana',
          experience: 'I was looking for freshers to hire. Filling details for job post was very simple and my job post was verified and started getting calls within a day. Quick and convenient!'
        },
        {
          name: 'Karthik Nair',
          title: 'Business Owner, Kerala',
          experience: 'Found a skilled electrician within hours of posting. The worker was professional and the booking process was seamless. Highly recommend UlcaApp!'
        },
        {
          name: 'Priya Sharma',
          title: 'Homeowner, Delhi',
          experience: 'Hired a plumber through UlcaApp and it was the easiest experience. Clear profiles, genuine ratings, and the worker arrived on time. Will definitely use again.'
        },
        {
          name: 'Venkat Rao',
          title: 'Contractor, Andhra Pradesh',
          experience: 'As a contractor I regularly need daily labour. UlcaApp gives me a steady pool of verified workers from my district. Saves me so much time and effort.'
        },
        {
          name: 'Meena Iyer',
          title: 'Homemaker, Karnataka',
          experience: 'I needed a cook urgently and UlcaApp helped me find one in my village the same day. The rating system gives me confidence in who I am hiring.'
        },
        {
          name: 'Suresh Babu',
          title: 'Shop Owner, Tamil Nadu',
          experience: 'Great app for finding skilled workers locally. I hired a painter and carpenter for my shop renovation. Both were professional and reasonably priced.'
        }
      ]);
    } finally {
      setLoadingTestimonials(false);
    }
  };

  const fetchJobRequests = useCallback(async () => {
    try {
      setLoadingJobs(true);
      // Try without status filter first to get all jobs
      const response = await axios.get(Api.jobRequests, { 
        params: { 
          // Fetch more than we show on Home so "View all" is reliable even after filtering
          // (we hide expired/non-open + hide current user's own jobs).
          limit: 25
        } 
      });
      console.log('Job requests API response:', response);
      console.log('Response data:', response.data);
      
      // Handle different response structures
      let jobs = [];
      if (Array.isArray(response.data)) {
        jobs = response.data;
      } else if (response.data?.jobRequests) {
        jobs = response.data.jobRequests;
      } else if (response.data?.data) {
        jobs = response.data.data;
      }
      
      // Filter to only show open and non-expired jobs on frontend
      let openJobs = jobs.filter(job => {
        const status = job.status || 'open';
        if (status !== 'open') return false;

        // Time-precise expiry: if expiryTime is set and has passed, hide the job
        if (job.expiryTime && new Date(job.expiryTime) <= new Date()) {
          return false;
        }

        // Fallback: hide jobs whose date+time window is entirely in the past
        const now = new Date();
        const endDate = job.endDate ? new Date(job.endDate) : null;
        const startDate = job.startDate ? new Date(job.startDate) : null;
        const effectiveDate = endDate || startDate;

        if (effectiveDate) {
          // If endTime is available, set exact time on the effective date
          if (job.endTime && /^\d{2}:\d{2}$/.test(job.endTime)) {
            const [hh, mm] = job.endTime.split(':').map(Number);
            effectiveDate.setHours(hh, mm, 0, 0);
          } else {
            effectiveDate.setHours(23, 59, 59, 999);
          }
          if (effectiveDate < now) {
            return false;
          }
        }

        return true;
      });

      // If a user is logged in, hide their own posted job requests
      let currentUserId = user?._id || user?.id;
      if (!currentUserId) {
        // Fallback: try to read from localStorage 'user' (used elsewhere in the app)
        try {
          const storedUser = JSON.parse(localStorage.getItem('user'));
          if (storedUser) {
            currentUserId = storedUser._id || storedUser.id;
          }
        } catch {
          // ignore JSON parse errors
        }
      }

      if (currentUserId) {
        openJobs = openJobs.filter(job => {
          const postedById =
            typeof job.postedBy === 'object'
              ? (job.postedBy?._id || job.postedBy?.id)
              : job.postedBy;
          if (!postedById) return true;
          return postedById.toString() !== currentUserId.toString();
        });
      }
      
      console.log('Final job requests to display:', openJobs.length, openJobs);
      setJobRequests(openJobs);
    } catch (error) {
      console.error('Error fetching job requests:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      // Set empty array on error so user sees the message
      setJobRequests([]);
    } finally {
      setLoadingJobs(false);
    }
  }, [user]);

  useEffect(() => {
    // Above-the-fold / interactive essentials — load immediately.
    // (villages + master skills feed the hero search; job requests is a primary
    // section with its own loading skeleton.)
    fetchVillages();
    fetchMasterSkills();
    fetchJobRequests();

    // Below-the-fold, non-critical data — defer until the browser is idle so it
    // never competes with first paint or hero interactivity. Falls back to a
    // short timeout on browsers without requestIdleCallback (e.g. Safari).
    const loadNonCritical = () => {
      fetchTestimonials();
      fetchFeaturedServices();
      fetchPopularServices();
    };

    let idleId;
    let timeoutId;
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(loadNonCritical, { timeout: 2500 });
    } else {
      timeoutId = setTimeout(loadNonCritical, 1200);
    }

    return () => {
      if (idleId != null && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) {
        clearTimeout(timeoutId);
      }
    };
  }, [fetchJobRequests]);

  // When a location is selected, dynamically narrow skills and worker name suggestions
  // to only those available in that location (and exclude "no-skill" workers server-side).
  useEffect(() => {
    let isMounted = true;

    const fetchLocationScopedOptions = async () => {
      const village = (location || '').trim();
      if (!village) {
        if (isMounted) {
          setLocationSkills([]);
          setLocationWorkerNames([]);
        }
        return;
      }

      try {
        const [skillsResp, namesResp] = await Promise.all([
          axios.get(Api.workerSkills, { params: { location: village } }),
          axios.get(Api.workerNames, { params: { location: village } })
        ]);

        if (!isMounted) return;

        const skills = Array.isArray(skillsResp.data?.skills) ? skillsResp.data.skills : [];
        const names = Array.isArray(namesResp.data?.names) ? namesResp.data.names : [];

        setLocationSkills(skills);
        setLocationWorkerNames(names);
      } catch (e) {
        console.error('Failed to load location-scoped options:', e);
        if (!isMounted) return;
        setLocationSkills([]);
        setLocationWorkerNames([]);
      }
    };

    fetchLocationScopedOptions();

    return () => {
      isMounted = false;
    };
  }, [location]);

  // Build the service options for the Home skill dropdown.
  // Uses master skills list (so newly added skills show up). If location is selected,
  // location-scoped skills are shown first (but we still keep the full master list).
  const serviceOptions = React.useMemo(() => {
    const byLowerName = new Map(
      (services || []).map((s) => [String(s?.name || '').toLowerCase(), s])
    );

    const baseNames =
      Array.isArray(masterSkills) && masterSkills.length > 0
        ? masterSkills
        : (services || []).map((s) => s?.name).filter(Boolean);

    const village = (location || '').trim();
    const preferredNames =
      village && Array.isArray(locationSkills) ? locationSkills.filter(Boolean) : [];

    const seen = new Set();
    const orderedNames = [];

    // Location skills first (if any)
    preferredNames.forEach((n) => {
      const name = String(n || '').trim();
      if (!name) return;
      const key = name.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      orderedNames.push(name);
    });

    // Then the master list (includes newly added skills)
    baseNames.forEach((n) => {
      const name = String(n || '').trim();
      if (!name) return;
      const key = name.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      orderedNames.push(name);
    });

    // Ensure the currently selected service stays visible in the dropdown list
    const current = String(service || '').trim();
    if (current) {
      const key = current.toLowerCase();
      if (!seen.has(key)) orderedNames.unshift(current);
    }

    return orderedNames.map((name) => {
      const key = String(name).toLowerCase();
      return byLowerName.get(key) || { name };
    });
  }, [location, locationSkills, masterSkills, service, services]);

  // Enquiry flow moved to `JobRequestEnquiry` page.

  const formatCurrency = (amount, currency = 'INR', paymentType = 'per_day') => {
    const formatted = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency
    }).format(amount);
    
    const paymentTypeLabels = {
      'per_hour': 'Per Hour',
      'per_person': 'Per Person',
      'per_day': 'Per Day',
      'half_day': 'Half Day',
      'per_sqft': 'Per Sqft'
    };
    
    return `${formatted} ${paymentTypeLabels[paymentType] || ''}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    // Date ONLY – time is handled separately using startTime/endTime
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    let animationFrameId;
    let scrollPosition = 0;
    const scrollSpeed = 1; // pixels per frame
    let timeoutId;
    let isRunning = false;

    // Function to start scrolling
    const startScrolling = () => {
      // Check if container has content
      if (scrollContainer.scrollWidth <= scrollContainer.clientWidth) {
        // Retry after a short delay if content not ready
        timeoutId = setTimeout(startScrolling, 200);
        return;
      }

      if (isRunning) return;
      isRunning = true;

      const scroll = () => {
        if (!isScrolling) {
          animationFrameId = requestAnimationFrame(scroll);
          return;
        }
        
        scrollPosition += scrollSpeed;
        
        // Get the actual scroll width
        const containerWidth = scrollContainer.scrollWidth;
        const maxScroll = containerWidth / 2;
        
        // Reset position when we've scrolled through one set of images
        if (scrollPosition >= maxScroll) {
          scrollPosition = 0;
        }
        
        scrollContainer.scrollLeft = scrollPosition;
        animationFrameId = requestAnimationFrame(scroll);
      };

      animationFrameId = requestAnimationFrame(scroll);
    };

    // Wait for images to load and container to be ready
    timeoutId = setTimeout(startScrolling, 500);

    // Also try when window loads
    const handleLoad = () => {
      if (!isRunning) {
        startScrolling();
      }
    };
    window.addEventListener('load', handleLoad);

    // Pause on hover
    const handleMouseEnter = () => setIsScrolling(false);
    const handleMouseLeave = () => setIsScrolling(true);

    scrollContainer.addEventListener('mouseenter', handleMouseEnter);
    scrollContainer.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      isRunning = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      window.removeEventListener('load', handleLoad);
      scrollContainer.removeEventListener('mouseenter', handleMouseEnter);
      scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isScrolling]);



  const buildHomeSearchQuery = ({ locationText, serviceText, nameText, teamSize }) => {
    const params = new URLSearchParams();
    const loc = (locationText || '').trim();
    const skill = (serviceText || '').trim();
    const workerName = (nameText || '').trim();
    if (loc) params.set('location', loc);
    if (skill) params.set('service', skill);
    if (workerName) params.set('name', workerName);
    if (teamSize > 1) params.set('minTeamSize', String(teamSize));
    return params.toString();
  };

  const navigateToWorkerSearch = ({ locationText, serviceText, nameText }) => {
    const minTeamSizeNum = Math.max(1, parseInt(minTeamSize, 10) || 1);
    const loc = (locationText || '').trim();
    const skill = (serviceText || '').trim();
    const workerName = (nameText || '').trim();
    sessionStorage.setItem('lastSearchParams', JSON.stringify({
      location: loc,
      service: skill,
      name: workerName,
      village: '',
      minTeamSize: minTeamSizeNum,
      includeContractors: true
    }));
    const query = buildHomeSearchQuery({
      locationText: loc,
      serviceText: skill,
      nameText: workerName,
      teamSize: minTeamSizeNum
    });
    navigate(`/search${query ? `?${query}` : ''}`);
  };

  const handleSearch = () => {
    const effectiveService = service || skillInputText.trim();
    navigateToWorkerSearch({
      locationText: location,
      serviceText: effectiveService,
      nameText: searchName
    });
  };

  const handleLocationSelected = (value) => {
    const loc = (value || '').trim();
    setLocation(loc);
    if (!loc) return;
    const effectiveService = service || skillInputText.trim();
    navigateToWorkerSearch({
      locationText: loc,
      serviceText: effectiveService,
      nameText: searchName
    });
  };

  // Voice search reuses the exact same search flow as manual typing. We navigate
  // with the spoken text directly (rather than relying on async state updates)
  // so recognition can trigger the search immediately, while also filling the
  // service field so the user can still edit the recognized text afterwards.
  const handleVoiceSearch = (transcript) => {
    const text = (transcript || '').trim();
    if (!text) return;
    setService(text);
    setSkillInputText(text);
    setSearchName('');
    const minTeamSizeNum = Math.max(1, parseInt(minTeamSize, 10) || 1);
    const searchParams = {
      location: location || '',
      service: text,
      name: '',
      village: '',
      minTeamSize: minTeamSizeNum,
      includeContractors: true
    };
    sessionStorage.setItem('lastSearchParams', JSON.stringify(searchParams));
    const query = buildHomeSearchQuery({
      locationText: location,
      serviceText: text,
      nameText: '',
      teamSize: minTeamSizeNum
    });
    navigate(`/search${query ? `?${query}` : ''}`);
  };

  // Explicit "Near Me" GPS search. Requests the owner's device location only on
  // tap; on grant we forward the coordinates to the results page which runs the
  // distance-based ($geoNear) search. Normal Search never requests GPS.
  const handleNearMe = async () => {
    const result = await requestLocation();
    if (!result.granted || !Array.isArray(result.coordinates)) {
      const msg = result.error === 'denied'
        ? 'Location permission is required for Near Me search. You can continue using normal search.'
        : result.error === 'unsupported'
          ? 'Your device/browser does not support location. You can continue using normal search.'
          : 'Could not get your current location. You can continue using normal search.';
      toast.info(msg);
      return;
    }
    const [lng, lat] = result.coordinates;
    const params = new URLSearchParams({
      near: 'true',
      lat: String(lat),
      lng: String(lng),
      radius: '5',
      sortBy: 'distance',
      sortOrder: 'asc'
    });
    navigate(`/search?${params.toString()}`);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#DDD0C8' }}>
      <SEO
        title="Hire Skilled Workers & Find Labour Jobs Near You"
        description="UlcaApp – India's #1 labour app to hire skilled workers near you. Find & book verified plumbers, electricians, painters, carpenters instantly. Workers: register free & find jobs near you."
        path="/"
      />
      <HomeHeroSearch
        location={location}
        setLocation={setLocation}
        service={service}
        setService={setService}
        skillInputText={skillInputText}
        setSkillInputText={setSkillInputText}
        searchName={searchName}
        setSearchName={setSearchName}
        minTeamSize={minTeamSize}
        setMinTeamSize={setMinTeamSize}
        services={serviceOptions}
        workerNames={location ? locationWorkerNames : []}
        villages={villages}
        onSearch={handleSearch}
        onVoiceSearch={handleVoiceSearch}
        onNearMe={handleNearMe}
        nearMeLoading={nearMeRequesting}
        onOpenLocationScreen={() => setLocationDialogOpen(true)}
        onOpenServiceSearchScreen={() => setServiceDialogOpen(true)}
      />

      {/* Mobile full-screen selectors */}
      <LocationSelectDialog
        open={locationDialogOpen}
        onClose={() => setLocationDialogOpen(false)}
        villages={villages}
        selectedLocation={location}
        onSelectLocation={handleLocationSelected}
      />

      <ServiceSearchDialog
        open={serviceDialogOpen}
        onClose={() => setServiceDialogOpen(false)}
        currentService={service}
        village={location}
        staticServices={services}
        minTeamSize={minTeamSize}
        onMinTeamSizeChange={(n) => setMinTeamSize(n)}
        onServiceSelected={(value) => {
          // Navigate directly to search page with service - no extra steps needed
          if (user) {
            // Use village parameter (same as location) for consistency with search page
            const minTeamSizeNum = Math.max(1, parseInt(minTeamSize, 10) || 1);
            const query = buildHomeSearchQuery({
              locationText: location,
              serviceText: value,
              nameText: '',
              teamSize: minTeamSizeNum
            });
            navigate(`/search${query ? `?${query}` : ''}`);
          } else {
            // If not logged in, prompt for login/register
            const token = localStorage.getItem('token');
            if (token) {
              navigate('/login');
            } else {
              navigate('/register');
            }
          }
        }}
      />

      <ServicesSection services={services} popularServices={popularServices} location={location} user={user} />

      <AvailableWorkersSection location={location} />

      <FeaturedServicesSection featuredServices={featuredServices} services={services} location={location} />

      <JobRequestsSection
        jobRequests={jobRequests}
        loadingJobs={loadingJobs}
        authLoading={loading}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
        user={user}
        isBasicProfileComplete={isBasicProfileComplete}
      />

      {/* Testimonials in their original position (all screen sizes). */}
      <Suspense fallback={<Box sx={{ minHeight: 200, bgcolor: '#FAF8F5' }} />}>
        <TestimonialsSection testimonials={testimonials} loading={loadingTestimonials} />
      </Suspense>

      {/* ===== SEO CONTENT SECTION ===== */}
      <Box
        component="section"
        sx={{ py: { xs: 3, md: 4 }, bgcolor: '#FAF8F5' }}
      >
        <Container maxWidth="md">
          <Typography
            component="h1"
            variant="h3"
            fontWeight="bold"
            textAlign="center"
            gutterBottom
            sx={{ color: '#7A4B2A', fontSize: { xs: '1.75rem', md: '2.25rem' } }}
          >
            UlcaApp – Universal Labour Connecting Advisory App
          </Typography>
          <Typography
            variant="body1"
            textAlign="center"
            sx={{ color: 'text.secondary', mb: 5, maxWidth: 700, mx: 'auto', lineHeight: 1.8 }}
          >
            UlcaApp is India's trusted <strong>labour app</strong> and <strong>worker app</strong> that connects customers with skilled workers. Whether you need a plumber, electrician, painter, or carpenter — find and hire verified workers near you, instantly.
          </Typography>

          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                component="h2"
                variant="h5"
                fontWeight="bold"
                gutterBottom
                sx={{ color: '#7A4B2A' }}
              >
                Hire Skilled Workers Near You
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                Looking for a <strong>plumber near me</strong>, <strong>electrician near me</strong>, or <strong>painter near me</strong>? UlcaApp makes it simple to search by location and service type. Browse worker profiles, check ratings, compare prices, and book the right professional for your job — all in one place. From <strong>AC repair</strong> and <strong>carpenter</strong> services to <strong>mason</strong>, <strong>cook</strong>, and <strong>cleaning services</strong>, UlcaApp covers every home and office need.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                component="h2"
                variant="h5"
                fontWeight="bold"
                gutterBottom
                sx={{ color: '#7A4B2A' }}
              >
                Find Labour Jobs & Work Near You
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                Are you a skilled worker looking for <strong>jobs for workers</strong> or <strong>labour jobs</strong> in your area? Register on UlcaApp for free, add your skills — plumbing, electrical, painting, carpentry, welding, or any trade — set your availability, and start receiving job requests from nearby customers. UlcaApp helps <strong>skilled workers near me</strong> connect with people who need their expertise.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                component="h2"
                variant="h5"
                fontWeight="bold"
                gutterBottom
                sx={{ color: '#7A4B2A' }}
              >
                Why Choose UlcaApp?
              </Typography>
              <Box component="ul" sx={{ color: 'text.secondary', pl: 2.5, '& li': { mb: 0.75 } }}>
                <li><strong>Verified Workers</strong> — Skilled, reviewed, and rated professionals</li>
                <li><strong>Location Based</strong> — Find workers or jobs near your location</li>
                <li><strong><TranslatedText>Easy Booking</TranslatedText></strong> — Select date, time, and book in seconds</li>
                <li><strong>All Services</strong> — Plumber, Electrician, Painter, Carpenter, AC Repair, Cook, Driver, and 15+ more</li>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                component="h2"
                variant="h5"
                fontWeight="bold"
                gutterBottom
                sx={{ color: '#7A4B2A' }}
              >
                How UlcaApp Works
              </Typography>
              <Box component="ol" sx={{ color: 'text.secondary', pl: 2.5, '& li': { mb: 0.75 } }}>
                <li><strong>Search</strong> — Enter your location and choose a service (plumber, electrician, painter, etc.)</li>
                <li><strong>Browse</strong> — View verified worker profiles with skills, ratings, and availability</li>
                <li><strong><TranslatedText>Book</TranslatedText></strong> — Select your preferred worker and book them for your job</li>
                <li><strong>Done</strong> — Get the job done and leave a review for the community</li>
              </Box>
            </Grid>
          </Grid>

          <Typography
            variant="body1"
            textAlign="center"
            sx={{ color: 'text.secondary', mt: 5, fontStyle: 'italic' }}
          >
            UlcaApp — Find the Right Workers. Get the Job Done. Available across India.
          </Typography>
        </Container>
      </Box>

      {/* Auto-Scrolling Images Carousel */}
      <Box sx={{ py: 6, bgcolor: '#DDD0C8', overflow: 'hidden', position: 'relative' }}>
        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Box
            ref={scrollContainerRef}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              overflowX: 'auto',
              overflowY: 'hidden',
              width: '100%',
              cursor: 'grab',
              '&:active': {
                cursor: 'grabbing',
              },
              // Hide scrollbar but allow scrolling
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              // Prevent user scrolling
              scrollBehavior: 'auto',
              // iOS Safari: enable momentum scrolling for smoothness
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {duplicatedImages.map((image, index) => (
              <Box
                key={index}
                sx={{
                  // iOS Safari resolves `flex-basis: auto` against content and
                  // ignores `min-width` here, which shrinks the box and makes
                  // `object-fit: cover` crop more. Pin the width via the flex
                  // shorthand so every device renders identical box sizes.
                  flex: {
                    xs: '0 0 200px',
                    sm: '0 0 250px',
                    md: '0 0 300px',
                  },
                  width: { xs: 200, sm: 250, md: 300 },
                  height: { xs: 150, sm: 180, md: 220 },
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                  },
                }}
              >
                <Box
                  component="img"
                  src={image}
                  alt={`Service ${index + 1}`}
                  loading="lazy"
                  decoding="async"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    display: 'block',
                  }}
                />
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Bottom CTA Section */}
      {!user && (
        <Box sx={{ bgcolor: '#FAF8F5', py: 6, borderTop: '1px solid #E7DED5' }}>
          <Container maxWidth="md">
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ color: '#7A4B2A' }}>
                Ready to Get Started?
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, color: '#9A6845' }}>
                Join thousands of satisfied customers and skilled workers
              </Typography>
              
              <Grid container spacing={3} justifyContent="center">
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card 
                    sx={{ 
                      p: 3, 
                      textAlign: 'center',
                      borderRadius: 3,
                      bgcolor: '#EDE6DF',
                      border: '1px solid #E7DED5',
                      boxShadow: '0 4px 20px rgba(122,75,42,0.08)',
                      transition: 'transform 0.2s',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 6px 24px rgba(122,75,42,0.16)' }
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: '#7A4B2A',
                        mx: 'auto',
                        mb: 2,
                        width: 60,
                        height: 60
                      }}
                    >
                      <Search fontSize="large" sx={{ color: '#FAF8F5' }} />
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#7A4B2A' }}>
                      Need a Service?
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 3, color: '#9A6845' }}>
                      Find and hire local workers for your projects
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => navigate('/register')}
                      sx={{
                        bgcolor: '#7A4B2A',
                        color: '#FAF8F5',
                        '&:hover': { bgcolor: '#6B3F22' },
                        borderRadius: 2,
                        py: 1.5
                      }}
                    >
                      Hire Workers
                    </Button>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card 
                    sx={{ 
                      p: 3, 
                      textAlign: 'center',
                      borderRadius: 3,
                      bgcolor: '#EDE6DF',
                      border: '1px solid #E7DED5',
                      boxShadow: '0 4px 20px rgba(122,75,42,0.08)',
                      transition: 'transform 0.2s',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 6px 24px rgba(122,75,42,0.16)' }
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: '#9A6845',
                        mx: 'auto',
                        mb: 2,
                        width: 60,
                        height: 60
                      }}
                    >
                      <Build fontSize="large" sx={{ color: '#FAF8F5' }} />
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#7A4B2A' }}>
                      Offer Services?
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 3, color: '#9A6845' }}>
                      Join as a worker and find local jobs
                    </Typography>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => navigate('/register')}
                      sx={{
                        borderColor: '#9A6845',
                        color: '#7A4B2A',
                        '&:hover': { 
                          borderColor: '#6B3F22',
                          bgcolor: 'rgba(122,75,42,0.06)'
                        },
                        borderRadius: 2,
                        py: 1.5
                      }}
                    >
                      Find Jobs
                    </Button>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Container>
        </Box>
      )}

      {/* Footer Section */}
      <Box
        component="footer"
        sx={{
          bgcolor: '#323232',
          color: '#DDD0C8',
          py: 3,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={2}>
            {/* Company Info */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#DDD0C8' }}>
                UlcaApp
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5, color: '#DDD0C8', opacity: 0.8 }}>
                Find the Right Workers for your needs, quickly and easily.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <IconButton
                  size="small"
                  aria-label="UlcaApp on Facebook"
                  sx={{
                    color: '#DDD0C8',
                    '&:hover': { color: '#DDD0C8', bgcolor: 'rgba(221, 208, 200, 0.1)' }
                  }}
                >
                  <Facebook fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="UlcaApp on Twitter"
                  sx={{
                    color: '#DDD0C8',
                    '&:hover': { color: '#DDD0C8', bgcolor: 'rgba(221, 208, 200, 0.1)' }
                  }}
                >
                  <Twitter fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="UlcaApp on Instagram"
                  sx={{
                    color: '#DDD0C8',
                    '&:hover': { color: '#DDD0C8', bgcolor: 'rgba(221, 208, 200, 0.1)' }
                  }}
                >
                  <Instagram fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="UlcaApp on LinkedIn"
                  sx={{
                    color: '#DDD0C8',
                    '&:hover': { color: '#DDD0C8', bgcolor: 'rgba(221, 208, 200, 0.1)' }
                  }}
                >
                  <LinkedIn fontSize="small" />
                </IconButton>
              </Box>
            </Grid>

            {/* Quick Links */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#DDD0C8' }}>
                Quick Links
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <Button
                  onClick={() => navigate('/')}
                  sx={{
                    color: '#DDD0C8',
                    opacity: 0.8,
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    py: 0.25,
                    minHeight: 0,
                    '&:hover': { opacity: 1, bgcolor: 'rgba(221, 208, 200, 0.1)' }
                  }}
                >
                  Home
                </Button>
                <Button
                  onClick={() => navigate('/search')}
                  sx={{
                    color: '#DDD0C8',
                    opacity: 0.8,
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    py: 0.25,
                    minHeight: 0,
                    '&:hover': { opacity: 1, bgcolor: 'rgba(221, 208, 200, 0.1)' }
                  }}
                >
                  Search Workers
                </Button>
                <Button
                  onClick={() => navigate('/about')}
                  sx={{
                    color: '#DDD0C8',
                    opacity: 0.8,
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    py: 0.25,
                    minHeight: 0,
                    '&:hover': { opacity: 1, bgcolor: 'rgba(221, 208, 200, 0.1)' }
                  }}
                >
                  About Us
                </Button>

                {isLoggedIn ? (
                  <>
                    <Button
                      onClick={() => navigate('/profile')}
                      sx={{
                        color: '#DDD0C8',
                        opacity: 0.8,
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        py: 0.25,
                        minHeight: 0,
                        '&:hover': { opacity: 1, bgcolor: 'rgba(221, 208, 200, 0.1)' }
                      }}
                    >
                      Account
                    </Button>
                    <Button
                      onClick={() => navigate('/bookings')}
                      sx={{
                        color: '#DDD0C8',
                        opacity: 0.8,
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        py: 0.25,
                        minHeight: 0,
                        '&:hover': { opacity: 1, bgcolor: 'rgba(221, 208, 200, 0.1)' }
                      }}
                    >
                      <TranslatedText>Bookings</TranslatedText>
                    </Button>
                    <Button
                      onClick={handleLogout}
                      sx={{
                        color: '#DDD0C8',
                        opacity: 0.8,
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        py: 0.25,
                        minHeight: 0,
                        '&:hover': { opacity: 1, bgcolor: 'rgba(221, 208, 200, 0.1)' }
                      }}
                    >
                      Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={() => navigate('/login')}
                      sx={{
                        color: '#DDD0C8',
                        opacity: 0.8,
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        py: 0.25,
                        minHeight: 0,
                        '&:hover': { opacity: 1, bgcolor: 'rgba(221, 208, 200, 0.1)' }
                      }}
                    >
                      Login
                    </Button>
                    <Button
                      onClick={() => navigate('/register')}
                      sx={{
                        color: '#DDD0C8',
                        opacity: 0.8,
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        py: 0.25,
                        minHeight: 0,
                        '&:hover': { opacity: 1, bgcolor: 'rgba(221, 208, 200, 0.1)' }
                      }}
                    >
                      Register
                    </Button>
                  </>
                )}
                <Button
                  onClick={() => navigate('/privacy-policy')}
                  sx={{
                    color: '#DDD0C8',
                    opacity: 0.8,
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    py: 0.25,
                    minHeight: 0,
                    '&:hover': { opacity: 1, bgcolor: 'rgba(221, 208, 200, 0.1)' }
                  }}
                >
                  Privacy Policy
                </Button>
                <Button
                  onClick={() => navigate('/terms-and-conditions')}
                  sx={{
                    color: '#DDD0C8',
                    opacity: 0.8,
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    py: 0.25,
                    minHeight: 0,
                    '&:hover': { opacity: 1, bgcolor: 'rgba(221, 208, 200, 0.1)' }
                  }}
                >
                  Terms & Conditions
                </Button>
                <Button
                  onClick={() => navigate('/feedback')}
                  sx={{
                    color: '#DDD0C8',
                    opacity: 0.8,
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    py: 0.25,
                    minHeight: 0,
                    '&:hover': { opacity: 1, bgcolor: 'rgba(221, 208, 200, 0.1)' }
                  }}
                >
                  Feedback
                </Button>
              </Box>
            </Grid>

            {/* Services */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#DDD0C8' }}>
                Services
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#DDD0C8', opacity: 0.8 }}>
                  <SkillLabel name="Plumber" />
                </Typography>
                <Typography variant="body2" sx={{ color: '#DDD0C8', opacity: 0.8 }}>
                  <SkillLabel name="Electrician" />
                </Typography>
                <Typography variant="body2" sx={{ color: '#DDD0C8', opacity: 0.8 }}>
                  <SkillLabel name="Painter" />
                </Typography>
                <Typography variant="body2" sx={{ color: '#DDD0C8', opacity: 0.8 }}>
                  <SkillLabel name="Carpenter" />
                </Typography>
                <Typography variant="body2" sx={{ color: '#DDD0C8', opacity: 0.8 }}>
                  And More...
                </Typography>
              </Box>
            </Grid>

            {/* Contact Info (phone removed) */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#DDD0C8' }}>
                Contact Us
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email sx={{ fontSize: 18, color: '#DDD0C8', opacity: 0.8 }} />
                  <Typography variant="body2" sx={{ color: '#DDD0C8', opacity: 0.8 }}>
                    support.ulcaapp@atozasindia.in
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <LocationIcon sx={{ fontSize: 18, color: '#DDD0C8', opacity: 0.8, mt: 0.5 }} />
                  <Typography variant="body2" sx={{ color: '#DDD0C8', opacity: 0.8 }}>
                    F-18, PH VI AYA NAGAR EXTN., NEW DELHI, Delhi, India - 110047
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Copyright */}
          <Box
            sx={{
              borderTop: '1px solid rgba(221, 208, 200, 0.2)',
              mt: 2,
              pt: 2,
              textAlign: 'center'
            }}
          >
            <Typography variant="body2" sx={{ color: '#DDD0C8', opacity: 0.7 }}>
              © {new Date().getFullYear()} UlcaApp. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Feedback dialog has been replaced by a dedicated /feedback screen. */}

      {/* Floating support chatbot widget */}
      <Suspense fallback={null}>
        <SupportChatbot />
      </Suspense>
    </Box>
  );
};

export default Home;